import React, { RefObject } from 'react';
import styled, { css } from '../../styles/styled-components';
import { Glyphicon, IconsKey } from '../icons';

export type InputProps = {
  value?: string;
  defaultValue?: string;
  readOnly?: boolean;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>, value: string) => any;
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => any;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  icon?: IconsKey | React.ReactNode;
  inputDomRef?: RefObject<HTMLInputElement>;
  right?: React.ReactNode;
  // label?: string;
  type?: JSX.IntrinsicElements['input']['type'];
  placeholder?: string;
  size?: Size;
} & Stylable;

export const Input: React.SFC<InputProps> = ({
  value,
  defaultValue,
  onChange,
  onKeyUp,
  inputDomRef,
  icon,
  right,
  onFocus,
  onBlur,
  size = 'normal',
  ...rest
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange(e, e.target.value);

  return (
    <Container {...rest}>
      <StyledInput
        inputDomRef={inputDomRef}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        onKeyUp={onKeyUp}
        onFocus={onFocus}
        onBlur={onBlur}
        size={size as any}
        {...rest}
      />
      <Right>
        {right}
        {icon && typeof icon === 'string' ? (
          <Glyphicon glyph={icon as IconsKey} />
        ) : (
          icon
        )}
      </Right>
    </Container>
  );
};

type StyledInputProps = {
  size: Size;
  inputDomRef: RefObject<HTMLInputElement>;
} & Omit<JSX.IntrinsicElements['input'], 'size'>;

const StyledInput = styled<StyledInputProps>(
  ({ size, inputDomRef, ...rest }) => <input ref={inputDomRef} {...rest} />,
)`
  // TODO MB Need to clean this thing up and get rid off important
  &&&&&&& {
    width: 100%;
    height: 100%;
    margin: 0;
    border-width: 1px !important;
    border-style: solid !important;
    border-color: ${p => p.theme.dividerColor} !important;
    border-radius: 5px !important;
    outline: none;

    &:hover {
      border-color: ${p => p.theme.terminalBg} !important;
    }

    &:focus,
    &:active {
      border-color: ${p => p.theme.primaryColor} !important;
    }

    // Size
    ${p => {
      switch (p.size) {
        case 'big':
          return css`
            padding: 12px 18px;
            font-size: 18px;
          `;
        case 'normal':
          return css`
            padding: 10px 15px;
            font-size: 16px;
          `;
        case 'small':
          return css`
            padding: 5px 8px;
            font-size: 14px;
          `;
      }

      const _exhaustiveCheck: never = p.size;
    }};
  }
`;

type ContainerProps = {};

const Container = styled<ContainerProps>(({ ...rest }) => <div {...rest} />)`
  display: inline-flex;
  position: relative;
  width: 100%;
`;

type LeftProps = {};

const Right = styled<LeftProps>(({ ...rest }) => <div {...rest} />)`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
`;
